import { useCallback, useEffect, useState } from 'react'
import { ApiError, assumedIdentity, platform, session } from '../lib/api'
import { countdown, expired, remainingMs } from '../lib/sessionClock'

/**
 * How long is left, ticking, in its own component.
 *
 * A leaf on purpose. The interval's state used to have nowhere to live but `ShellBody`, and putting
 * it there would re-render the masthead, the rail, the query bar and whatever screen is open once a
 * second for half an hour. Here it re-renders a span.
 *
 * `aria-hidden` because the banner around it is a live region: a number announced every tick is a
 * screen reader saying nothing useful very often. The sentence beside it stays readable and stable,
 * and it is the sentence that carries the meaning.
 */
function Countdown({ expiresUtc }: { expiresUtc: string }) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(tick)
  }, [])

  const remaining = remainingMs(expiresUtc, now)
  if (remaining === null) return null

  return (
    <strong aria-hidden="true" data-testid="assumed-remaining">
      {countdown(remaining)}
    </strong>
  )
}

/** Below this, the end is close enough that somebody should be offered a way to carry on. */
const OFFER_EXTENSION_WITHIN_MS = 5 * 60 * 1000

/**
 * The band shown while a platform administrator is inside somebody else's organization.
 *
 * Deliberately loud, deliberately not dismissible, and on every screen rather than the one the
 * session started on. Forgetting which organization you are in is the failure this designs against.
 *
 * What is new is that it now says *when* it ends. It always claimed the session "ends by itself" and
 * never said how long was left, so the only way to find out was to be thrown out mid-sentence - and
 * an assumed session is the shortest one in the product.
 *
 * Extending opens a **new** support session rather than renewing this one, which is the whole point.
 * ADR-S-043 caps these at thirty minutes because an administrator who forgets to hand an organization
 * back is the normal case and the clock is the only thing that reliably closes that window. A silent
 * renewal would remove it. Assuming again writes a second row in the customer's own record (D-289),
 * so an extension is a decision somebody took and the customer can read.
 */
export function AssumedSessionBanner({
  tenantName,
  assumedByName,
  expiresUtc,
  onReleaseTenant,
  onExtended
}: {
  tenantName: string | null
  assumedByName: string
  expiresUtc: string | null
  onReleaseTenant: () => void
  onExtended: () => void
}) {
  const [asking, setAsking] = useState(false)
  const [reason, setReason] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  // Coarse on purpose: this only decides whether to offer a button, so a second-by-second tick would
  // be a re-render of the banner every second for the twenty-five minutes before it matters.
  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 15_000)
    return () => window.clearInterval(tick)
  }, [])

  const remaining = remainingMs(expiresUtc, now)
  const nearlyOver = remaining !== null && remaining <= OFFER_EXTENSION_WITHIN_MS

  // The platform session parked behind this one is what may mint another support session; the active
  // token carries the customer's roles and never platform-admin, so the endpoint would refuse it.
  // If that session has ended too there is nothing to extend with, and offering a button whose only
  // possible answer is a refusal wastes the little time left.
  const parked = session.parked()
  const canExtend = parked !== null && !expired(parked.expiresUtc, now)

  const extend = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      const identity = session.identity()
      const platformSession = session.parked()
      if (!identity || !platformSession) return

      setBusy(true)
      setProblem(null)
      try {
        const assumed = await platform.extendAssumed(
          identity.tenantId,
          identity.roles[0] ?? 'viewer',
          reason,
          platformSession.token
        )
        // Saved without parking: the platform session is already parked, and `assume` would bury it
        // a level deeper with no way back to it.
        session.reassume(assumedIdentity(assumed, assumedByName))
        setAsking(false)
        setReason('')
        onExtended()
      } catch (error) {
        setProblem(error instanceof ApiError ? error.message : 'The session could not be extended.')
      } finally {
        setBusy(false)
      }
    },
    [reason, assumedByName, onExtended]
  )

  return (
    <div className="assumed-banner" role="status" data-testid="assumed-banner">
      <span>
        Viewing <strong>{tenantName ?? 'another organization'}</strong> as {assumedByName}. This
        session is recorded and ends by itself
        {expiresUtc ? (
          <>
            {' '}
            in <Countdown expiresUtc={expiresUtc} />
          </>
        ) : null}
        .
      </span>

      <span className="assumed-actions">
        {/*
          Offered only near the end. A button to extend something that has twenty-five minutes left is
          an invitation to hold an organization open for longer than the work needs, which is the
          habit the thirty-minute cap exists to break.
        */}
        {nearlyOver && !asking && canExtend && (
          <button type="button" onClick={() => setAsking(true)} data-testid="extend-assumed">
            Extend
          </button>
        )}

        {nearlyOver && !canExtend && (
          <span data-testid="extend-unavailable">
            Your platform session has ended too. Return and sign in again.
          </span>
        )}

        <button type="button" onClick={onReleaseTenant} data-testid="release-tenant">
          Return to platform
        </button>
      </span>

      {asking && (
        <form onSubmit={extend} className="assumed-extend" data-testid="extend-form">
          <label htmlFor="extend-reason">
            Why this needs longer. The organization reads this, beside the reason you gave to open it.
          </label>
          <input
            id="extend-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            data-testid="extend-reason"
            required
            minLength={8}
          />

          {problem && (
            <p className="error" role="alert" data-testid="extend-problem">
              {problem}
            </p>
          )}

          <span className="assumed-actions">
            <button type="button" onClick={() => setAsking(false)} disabled={busy}>
              Cancel
            </button>
            <button type="submit" disabled={busy} data-testid="confirm-extend">
              {busy ? 'Extending…' : 'Extend the session'}
            </button>
          </span>
        </form>
      )}
    </div>
  )
}
